import { CommonColumnProperties, CustomColumns, ImportWizard, Modal, Table, Title } from "components";
import { ValidationState } from "components/ImportWizard/ImportWizardTypes";
import { WorkerResponse, runBatchWorkers } from "components/WebWorkers/ZWebWorker";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { cleanDepartmentDetails, createDepartments, deleteDepartment, getDepartments, updateDepartmentField, updateDepartments } from "redux/slices/Departments";
import { cleanSubDepartmentDetails, createSubDepartments, getSubDepartments, updateSubDepartments } from "redux/slices/SubDepartment";
import { generalSync } from "redux/slices/Sync";
import { showToast } from "redux/slices/Toasts";
import { RootState } from "redux/store";
import { DepartmentsImportWorkerData, DepartmentsImportWorkerResponseData } from "./departmentsImportWorker";
import { SubdepartmentsImportWorkerData, SubdepartmentsImportWorkerResponseData } from "./subdepartmentsImportWorker";

export function Departments() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [importPercentage, setImportPercentage] = useState<number>(0);
	const departments = useAppSelector((state: RootState) => state.department);
	const subdepartments = useAppSelector((state: RootState) => state.subDepartment);
	const [showPopup, setShowPopup] = useState<boolean>(false);
	const user = useAppSelector((state: RootState) => state.logedinUser);
	const columnsb = [
		{
			Header: "No.",
			accessor: "number",
			defaultCanSort: true,
			width: 60,
			Cell: (row: any) => user.permissions.departments.canUpdate ? <Link to={`/app/department/${row.cell.row.original.id}`}>{row.value}</Link> : <>{row.value}</>,

		},
		{
			Header: "Name",
			accessor: "name",
			defaultCanSort: true,
			width: 200,
			Cell: (row: any) => user.permissions.departments.canUpdate ? <Link to={`/app/department/${row.cell.row.original.id}`}>{row.value}</Link> : <>{row.value}</>,
		},
		{
			Header: "Status",
			accessor: "lock",
			width: 800,
			Cell: (row: any) => {
				return <>
					{row.cell.row.original.lock === false && <Badge className="me-1 badge-outline-primary">Unlocked</Badge>}
					{row.cell.row.original.lock === true && <Badge className="me-1 badge-outline-danger">Locked </Badge>}
				</>;
			}
		},
	];

	useEffect(() => {
		dispatch(getDepartments());
		dispatch(cleanDepartmentDetails());
		dispatch(cleanSubDepartmentDetails());

	}, []);

	const selectedRows = useCallback((pageIndex: any) => {
		dispatch(updateDepartmentField({ key: "selectedRows", value: pageIndex }));
	}, []);


	function handleDeleteDepartment() {
		departments.selectedRows.forEach((row: any) => {
			// @ts-ignore
			dispatch(deleteDepartment({ ids: [departments.table[parseInt(row)].id] })).then(() => {
				dispatch(getDepartments());
			});
		});
	}

	function Sync() {
		dispatch(generalSync({ departments: true })).then((res) => {
			if (res.data) {
				dispatch(showToast({ value: ["Sync Completed"] }));
			} else {
				dispatch(showToast({ value: ["Sync Failed"] }));
			}
		})
	}

	function handleChange(key: string, value: any) {
		dispatch(updateDepartmentField({ key: key, value: value }));
	}

	const importWizardExtraMatches: CustomColumns[] = [
		{
			title: "Dept #",
			key: "DeptNo",
			required: false,
			display: true,
			validation: (value: any) => ({ valid: true, error: "" }),
			autoMatch: ["dept#", "dept #"]
		},
		{
			title: "Department Name",
			key: "DepartmentName",
			required: true,
			display: true,
			validation: (value: any) => value !== "" || value !== undefined ? ({ valid: true, error: "" }) : ({ valid: false, error: "Department Name cannot be blank" }),
			autoMatch: ["dept", "department", "department name"]
		},
		{
			title: "Sub Dept #",
			key: "SubDeptNo",
			required: false,
			display: true,
			validation: (value: any) => ({ valid: true, error: "" }),
			autoMatch: ["subdept#", "sub dept #"]
		},
		{
			title: "Sub Department Name",
			key: "SubDepartmentName",
			required: false,
			display: true,
			validation: (value: any) => ({ valid: true, error: "" }),
			autoMatch: ["subdept", "sub department", "sub department name", "subdepartment name"]
		},

	]

	function WholeTableValidation(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		let temp: CommonColumnProperties[] = [...rows];

		let IsValid = (text: any) => text !== undefined && text !== null && text !== "";

		for (let index = 0; index < temp.length; index++) {
			const row = temp[index];

			if (row.hasError === undefined || row.hasError === false) { //don"t bother validating bad rows

				/*
				1. Check that the department name is not empty
				2. Same department no but different name
				3. Same department name but different no
				4. Same subdepartment name, same department name but different subdepartment no
				*/

				//1. 
				if (!IsValid(row.DepartmentName)) {
					row.message = "Blank Department Name";
					row.hasError = true;
					row.validationState = ValidationState.Invalid;
					continue;
				}

				//2.
				var sameDepartmentNo = temp.filter(i => row !== i
					&& (i.DeptNo !== null && i.DeptNo !== undefined && i.DeptNo !== '')
					&& i.DeptNo?.toLocaleLowerCase() === row.DeptNo?.toLocaleLowerCase()
					&& i.DepartmentName?.toLocaleLowerCase() !== row.DepartmentName?.toLocaleLowerCase());
				if (sameDepartmentNo.length > 0) {
					row.message = "Department name differs for same department number";
					row.hasError = true;
					row.validationState = ValidationState.Invalid;
					continue;
				}

				//3.
				var sameDepartmentNo = temp.filter(i => row !== i
					&& (i.DeptNo !== null && i.DeptNo !== undefined && i.DeptNo !== '')
					&& i.DeptNo?.toLocaleLowerCase() !== row.DeptNo?.toLocaleLowerCase()
					&& i.DepartmentName?.toLocaleLowerCase() === row.DepartmentName?.toLocaleLowerCase());
				if (sameDepartmentNo.length > 0) {
					row.message = "Department number differs for same department";
					row.hasError = true;
					row.validationState = ValidationState.Invalid;
					continue;
				}

				//4. 
				if (IsValid(row.SubDepartmentName)) {
					var sameDepartmentNo = temp.filter(i => row !== i
						&& (i.SubDeptNo !== null && i.SubDeptNo !== undefined && i.SubDeptNo !== '')
						&& i.SubDepartmentName?.toLocaleLowerCase() == row.SubDepartmentName?.toLocaleLowerCase()
						&& i.DepartmentName?.toLocaleLowerCase() === row.DepartmentName?.toLocaleLowerCase()
						&& i.SubDeptNo?.toLocaleLowerCase() !== row.SubDeptNo?.toLocaleLowerCase());
					if (sameDepartmentNo.length > 0) {
						row.message = "Sub department number differs for same sub department"
						row.hasError = true;
						row.validationState = ValidationState.Invalid;
						continue;
					}
				}
			}

		};

		return temp;
	}

	const terminateWorkersRef = useRef<Function>();

	async function importDepartmentsAsync(rows: CommonColumnProperties[]) {
		/*
		--Process--
			1. Load the departments
			2. Loop through, adding new ones and updating old
			3. Save the departments
			4. Load the departments again and subdepartments
			5. Loop through, adding new subdepartments and updating old
			6. Save subdepartments
		*/

		//Department section
		let loadedDepartments = await dispatch(getDepartments());

		const deptWebWorkerData: DepartmentsImportWorkerData = {
			data: rows,
			loadedDepartments: loadedDepartments.data
		}
		const errors: Map<string, string> = new Map<string, string>();

		const { runWorkers: runDepartmentWorkers, terminateWorkers: terminateDepartmentWorkers } = runBatchWorkers<DepartmentsImportWorkerData, CommonColumnProperties, DepartmentsImportWorkerResponseData>({
			workerData: deptWebWorkerData,
			createWorker: () => new Worker(new URL('departmentsImportWorker.ts', import.meta.url), { type: "module" }),
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { departName, error } object for each department and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const errorEntry = errors.get(r.DepartmentName);
							errors.set(r.DepartmentName ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				}

				setImportPercentage((prev) => Math.min(100, prev + ((1 / responseContext.numBatches) * 100)));
			}, 
			onWorkersFinished: async (workerResponses: WorkerResponse<DepartmentsImportWorkerResponseData>[]) => {
				let successfulResponses = workerResponses.filter(r => r.data != null);

				let newDeparts = successfulResponses.flatMap(r => r.data?.newDepts).removeDuplicates(dept => dept.name);
				let updateDeparts = successfulResponses.flatMap(r => r.data?.updateDepts).removeDuplicates(dept => dept.id);

				//Save departments
				let createDeptResponse = await dispatch(createDepartments(newDeparts)).catch((error) => {
					console.log(error);
					const createDeptError = error;
					newDeparts.forEach(d => {
						const errorEntry = errors.get(d.name);
						errors.set(d.name, errorEntry ? errorEntry + ", " + createDeptError : createDeptError);
					});

				});

				let updateDeptResponse = await dispatch(updateDepartments(updateDeparts)).catch((error) => {
					const updateDeptError = error;
					updateDeparts.forEach(d => {
						const errorEntry = errors.get(d.name);
						errors.set(d.name, errorEntry ? errorEntry + ", " + updateDeptError : updateDeptError);
					});
				});

				setImportPercentage(0); //Reset the import percentage
			},
			onWorkerError: (error, unprocessedData) => {
				unprocessedData.forEach(data => {
					const errorEntry = errors.get(data.DepartmentName);
					const errorMessage = error.message ?? "Unknown error occurred";
					errors.set(data.DepartmentName ?? "", errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
				})
			}
		});

		terminateWorkersRef.current = terminateDepartmentWorkers;
		
		await runDepartmentWorkers;

		//Sub department time!!
		loadedDepartments = await dispatch(getDepartments());
		let loadedSubdepartments = await dispatch(getSubDepartments(undefined));

		const subDeptWebWorkerData: SubdepartmentsImportWorkerData = {
			data: rows,
			loadedDepartments: loadedDepartments.data,
			loadedSubdepartments: loadedSubdepartments.data
		}

		const { runWorkers: runSubdepartmentWorkers, terminateWorkers: terminateSubdepartmentWorkers } = runBatchWorkers<SubdepartmentsImportWorkerData, CommonColumnProperties, SubdepartmentsImportWorkerResponseData>({
			workerData: subDeptWebWorkerData, 
			createWorker: () => new Worker(new URL('subdepartmentsImportWorker.ts', import.meta.url), { type: "module" }), 
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { departName, error } object for each subdepartment and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const deptAndSubDept = `${r.DepartmentName} > ${r.SubDepartmentName}`;
							const errorEntry = errors.get(deptAndSubDept);
							errors.set(deptAndSubDept ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				}

				setImportPercentage((prev) => Math.min(100, prev + ((1 / responseContext.numBatches) * 100)));
			}, 
			onWorkersFinished: async (workerResponses: WorkerResponse<SubdepartmentsImportWorkerResponseData>[]) => {
				let successfulResponses = workerResponses.filter(r => r.data != null);
				let newSubDeparts = successfulResponses.flatMap(r => r.data?.newSubDepts).removeDuplicates(subDept => subDept.name);
				let updateSubDeparts = successfulResponses.flatMap(r => r.data?.updateSubDepts).removeDuplicates(subDept => subDept.id);

				//Save Subdepartments
				let createSubDeptResponse = await dispatch(createSubDepartments(newSubDeparts)).catch((error) => {
					const createSubdeptError = error;
					newSubDeparts.forEach(d => {
						const deptAndSubDept = `${d.departmentName} > ${d.name}`;
						const errorEntry = errors.get(deptAndSubDept);
						errors.set(deptAndSubDept, errorEntry ? errorEntry + ", " + createSubdeptError : createSubdeptError);
					});
				});

				let updateSubDeptResponse = await dispatch(updateSubDepartments(updateSubDeparts)).catch((error) => {
					const updateSubdeptError = error;
					updateSubDeparts.forEach(d => {
						const deptAndSubDept = `${d.departmentName} > ${d.name}`;
						const errorEntry = errors.get(deptAndSubDept);
						errors.set(deptAndSubDept, errorEntry ? errorEntry + ", " + updateSubdeptError : updateSubdeptError);
					});
				});

				setImportPercentage(0); //Reset the import percentage
			}, 
			onWorkerError: (error, unprocessedData) => {
				unprocessedData.forEach(data => {
					const deptAndSubDept = `${data.DepartmentName} > ${data.SubDepartmentName}`;
					const errorEntry = errors.get(deptAndSubDept);
					const errorMessage = error.message ?? "Unknown error occurred";
					errors.set(deptAndSubDept ?? "", errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
				})
			}
		});

		terminateWorkersRef.current = terminateSubdepartmentWorkers;

		await runSubdepartmentWorkers;

		await dispatch(getDepartments());
		const returnErrors: { departName: string, error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				departName: key,
				error: value,
			})
		});

		return [...returnErrors];
	}

	return (
		<>
			<Title pageTitle="Departments" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<div className="page-container">
									<ImportWizard
										title="Departments/Sub Departments"
										display={departments.bulkEditPopup}
										handleClose={() => { 
											if (terminateWorkersRef.current) terminateWorkersRef.current();
											handleChange("bulkEditPopup", false);
										}}
										importAsync={(rows) => importDepartmentsAsync(rows)}
										matchAgainst={"None"}
										customColumns={importWizardExtraMatches}
										validateTable={(rows) => WholeTableValidation(rows)}
										finish={() => handleChange("bulkEditPopup", false)}
										importPercentage={importPercentage}
										customPostImportColumnDef={{
											errorKey: "error",
											field: {
												key: "departName",
												header: "Department/Subdepartment Name"
											}
										}}
									/>
									<Table
										pageSize={100}
										isSortable={true}
										pagination={true}
										isSelectable={true}
										isSearchable={true}
										columns={columnsb}
										data={departments.table}
										errors={departments.error}
										selectedRows={selectedRows}
										loading={departments.loading}
										singleImport={user.permissions.departments.canImport ? () => handleChange("bulkEditPopup", true) : undefined}
										removeRows={user.permissions.departments.canDelete ? handleDeleteDepartment : undefined}
										syncOptions={() => setShowPopup(true)}
										reloadTableRows={() => dispatch(getDepartments())}
										addNewRow={user.permissions.departments.canCreate ? () => navigate("/app/department/new") : undefined}
									/>
								</div>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<Modal
				show={showPopup}
				title="Manual Sync"
				description="Do you want to resync all Departments/Sub Departments?"
				buttonText="Sync"
				buttonType="primary"
				handleClose={() => setShowPopup(false)}
				handleSubmit={() => {
					setShowPopup(false);
					Sync();
				}}
			/>
		</>
	);
}


